/*
 * @Copyright 2026. Institute for Future Intelligence, Inc.
 *
 * Thermostat GROUPS — thermostats that act on a chosen set of atoms instead
 * of the whole system, each with its own setpoint. Shared by the crystal
 * engine (CrystalDynamics) and the molecular lab engine (MolecularDynamics).
 *
 * This is what makes the classic nonequilibrium experiments possible: hold
 * one end of a slab hot and the other cold and watch a temperature gradient
 * and a heat current develop (heat conduction); thermostat only the
 * substrate while a film grows on it; heat one spot and watch the energy
 * spread. Every thermostat the labs had acted on every atom, which rules all
 * of these out.
 *
 * A group is a SPEC the user or agent writes (ProjectState.thermostatGroups,
 * see ThermostatGroupSpec) and a RESOLVED index list the engine applies. The
 * spec is resolved against the live atoms every control update (each MD
 * chunk), so a region group follows the atoms that are in the region NOW,
 * the way a region-scoped thermostat does in a production MD code — an atom
 * that diffuses out of the hot zone stops being heated.
 *
 * Semantics (both engines):
 *   - each enabled group is thermostatted to its own setpoint with the lab's
 *     thermostat algorithm (Berendsen / CSVR in the crystal lab; velocity
 *     rescale / CSVR / Langevin in the molecular lab), applied to its atoms
 *     only;
 *   - atoms in NO group follow the global setting: the heat bath at the
 *     global setpoint when constantTemperature is on, otherwise NVE — so the
 *     middle of a slab between a hot and a cold end conducts heat without
 *     any thermostat touching it;
 *   - an atom in several groups belongs to the FIRST one listed;
 *   - a fixed (pinned) or cut atom is never thermostatted.
 *
 * Regions use the same world-frame vocabulary as agent placement
 * (regionGeometry.ts): bottom/top = −Z/+Z, left/right = −X/+X, back/front =
 * −Y/+Y. `fraction` is the depth of the band measured from the named face as
 * a fraction of the atoms' extent along that axis (not the container's —
 * the slab examples leave vacuum above the crystal, and a gas does not fill
 * its box to the walls). Several tokens intersect: ["bottom","left"] is the
 * lower-left edge region.
 */

import { UNIT_EV_OVER_KB } from './physicalConstants.ts';

/** World-frame region faces, as the agent's placement vocabulary names them. */
/**
 * Faces, plus two band shapes per axis for the periodic heat-conduction
 * layout: '<axis>middle' is a band of depth `fraction` centered on the
 * structure's midpoint along that axis, '<axis>edges' is the two half-depth
 * bands at both ends (one band straddling the periodic boundary).
 */
export const THERMOSTAT_REGION_TOKENS = [
  'bottom',
  'top',
  'left',
  'right',
  'front',
  'back',
  'zmiddle',
  'zedges',
  'xmiddle',
  'xedges',
  'ymiddle',
  'yedges',
] as const;
export type ThermostatRegionToken = (typeof THERMOSTAT_REGION_TOKENS)[number];

type Axis = 'x' | 'y' | 'z';
const REGION_FACE: Partial<Record<ThermostatRegionToken, { axis: Axis; sign: 1 | -1 }>> = {
  bottom: { axis: 'z', sign: -1 },
  top: { axis: 'z', sign: 1 },
  left: { axis: 'x', sign: -1 },
  right: { axis: 'x', sign: 1 },
  back: { axis: 'y', sign: -1 },
  front: { axis: 'y', sign: 1 },
};

/** Default band depth: a quarter of the extent from the named face. */
export const DEFAULT_THERMOSTAT_REGION_FRACTION = 0.25;

/**
 * One thermostat group as stored on ProjectState.thermostatGroups. Membership
 * is, in order of precedence: explicit `atoms` (engine indices) when given;
 * otherwise the atoms inside `region` (all atoms when no region is named),
 * narrowed to `elements` when that is given.
 */
export interface ThermostatGroupSpec {
  /** Stable id (for the UI list and the per-group readout). */
  id: string;
  /** Display name, e.g. "hot end". */
  name?: string;
  /** Setpoint in kelvin. */
  temperature: number;
  /** Default true. A disabled group is kept in the list but not applied. */
  enabled?: boolean;
  /** World-frame faces whose bands intersect to form the region. */
  region?: ThermostatRegionToken[];
  /** Band depth from each named face, as a fraction (0–1) of the atoms' extent. */
  fraction?: number;
  /** Explicit engine atom indices; wins over region / elements. */
  atoms?: number[];
  /** Element symbols to keep (e.g. ['Cu']); combined with the region. */
  elements?: string[];
}

/** A group with its membership resolved to engine atom indices. */
export interface ResolvedThermostatGroup {
  id: string;
  name: string;
  temperature: number;
  indices: number[];
}

/** The slice of Atom the resolver reads — the two engines share the Atom class. */
export interface ThermostatAtomLike {
  position: { x: number; y: number; z: number };
  velocity?: { lengthSq(): number };
  mass?: number;
  fixed?: boolean;
  removed?: boolean;
  elementSymbol?: string;
  /** Atom.getKineticEnergy — the default kinetic-energy reader below. */
  getKineticEnergy?: () => number;
}

/** Kinetic energy of an atom-like, eV: its own method, else 0. */
export const kineticEnergyOfAtom = (a: ThermostatAtomLike): number => (a.getKineticEnergy ? a.getKineticEnergy() : 0);

let nextGroupSeq = 1;
/** A fresh spec with a unique id; `partial` overrides the defaults. */
export const newThermostatGroup = (partial: Partial<ThermostatGroupSpec> = {}): ThermostatGroupSpec => ({
  id: `tg${Date.now().toString(36)}${(nextGroupSeq++).toString(36)}`,
  temperature: 300,
  region: ['bottom'],
  fraction: DEFAULT_THERMOSTAT_REGION_FRACTION,
  ...partial,
});

/** Human label for a group's membership rule. */
export const describeThermostatGroupMembership = (g: ThermostatGroupSpec): string => {
  if (g.atoms && g.atoms.length > 0) return `${g.atoms.length} picked atom${g.atoms.length === 1 ? '' : 's'}`;
  const parts: string[] = [];
  if (g.region && g.region.length > 0) {
    const f = Math.round(100 * (g.fraction ?? DEFAULT_THERMOSTAT_REGION_FRACTION));
    parts.push(`${g.region.join('+')} ${f}%`);
  } else parts.push('everywhere');
  if (g.elements && g.elements.length > 0) parts.push(g.elements.join(','));
  return parts.join(' · ');
};

/** The name the readouts use when the spec has none. */
export const thermostatGroupName = (g: ThermostatGroupSpec, index: number): string =>
  g.name && g.name.trim().length > 0 ? g.name.trim() : `group ${index + 1}`;

/**
 * Resolve specs to engine indices against the live atoms. Only present,
 * movable atoms are members; an atom goes to the first group that claims
 * it; disabled and empty groups are dropped. Pure — no engine state.
 */
export const resolveThermostatGroups = (
  atoms: readonly ThermostatAtomLike[],
  specs: readonly ThermostatGroupSpec[] | undefined | null,
): ResolvedThermostatGroup[] => {
  const out: ResolvedThermostatGroup[] = [];
  if (!specs || specs.length === 0 || atoms.length === 0) return out;
  const live = (a: ThermostatAtomLike) => !a.removed && !a.fixed;
  // Extent of the present atoms (fixed ones included — a pinned substrate is
  // still where the structure is) along each axis, for the region bands.
  let extent: { min: Record<Axis, number>; max: Record<Axis, number> } | null = null;
  const extentOf = () => {
    if (extent) return extent;
    const min = { x: Infinity, y: Infinity, z: Infinity };
    const max = { x: -Infinity, y: -Infinity, z: -Infinity };
    for (const a of atoms) {
      if (a.removed) continue;
      const p = a.position;
      if (p.x < min.x) min.x = p.x;
      if (p.y < min.y) min.y = p.y;
      if (p.z < min.z) min.z = p.z;
      if (p.x > max.x) max.x = p.x;
      if (p.y > max.y) max.y = p.y;
      if (p.z > max.z) max.z = p.z;
    }
    extent = { min, max };
    return extent;
  };
  const claimed = new Uint8Array(atoms.length);
  specs.forEach((g, gi) => {
    if (g.enabled === false) return;
    const indices: number[] = [];
    if (g.atoms && g.atoms.length > 0) {
      for (const i of g.atoms) {
        const a = atoms[i];
        if (!a || !live(a) || claimed[i]) continue;
        indices.push(i);
      }
    } else {
      const fraction = Math.min(1, Math.max(0.01, g.fraction ?? DEFAULT_THERMOSTAT_REGION_FRACTION));
      const elements = g.elements && g.elements.length > 0 ? new Set(g.elements) : null;
      // One membership test per token; a point is in the region when it
      // passes every test (faces intersect). A one-layer structure (len 0)
      // is entirely in every band.
      const tests: ((v: number) => boolean)[] = [];
      const testAxes: Axis[] = [];
      for (const tk of g.region ?? []) {
        const face = REGION_FACE[tk];
        const { min, max } = extentOf();
        if (face) {
          const { axis, sign } = face;
          const depth = fraction * (max[axis] - min[axis]);
          testAxes.push(axis);
          tests.push(sign < 0 ? (v) => v <= min[axis] + depth + 1e-9 : (v) => v >= max[axis] - depth - 1e-9);
          continue;
        }
        const m = /^([xyz])(middle|edges)$/.exec(tk);
        if (!m) continue;
        const axis = m[1] as Axis;
        const len = max[axis] - min[axis];
        const mid = (min[axis] + max[axis]) / 2;
        const half = (fraction * len) / 2;
        testAxes.push(axis);
        tests.push(
          m[2] === 'middle'
            ? (v) => v >= mid - half - 1e-9 && v <= mid + half + 1e-9
            : (v) => v <= min[axis] + half + 1e-9 || v >= max[axis] - half - 1e-9,
        );
      }
      for (let i = 0; i < atoms.length; i++) {
        const a = atoms[i];
        if (!live(a) || claimed[i]) continue;
        if (elements && !elements.has(a.elementSymbol ?? '')) continue;
        let inside = true;
        for (let k = 0; k < tests.length; k++) {
          if (!tests[k](a.position[testAxes[k]])) {
            inside = false;
            break;
          }
        }
        if (inside) indices.push(i);
      }
    }
    if (indices.length === 0) return;
    for (const i of indices) claimed[i] = 1;
    out.push({ id: g.id, name: thermostatGroupName(g, gi), temperature: Math.max(0, g.temperature), indices });
  });
  return out;
};

/**
 * The readouts' view: every spec resolved against the atoms right now, with
 * its measured temperature and member count. Used by both popovers and the
 * agent's state reads, so the number shown is for the atoms that are in the
 * region at this moment — the same membership the engines apply.
 */
export const liveGroupTemperatures = (
  atoms: readonly ThermostatAtomLike[],
  specs: readonly ThermostatGroupSpec[] | undefined | null,
  kineticEnergyOf: (a: ThermostatAtomLike) => number = kineticEnergyOfAtom,
): { id: string; name: string; setpointK: number; temperatureK: number; atomCount: number }[] =>
  resolveThermostatGroups(atoms, specs).map((g) => ({
    id: g.id,
    name: g.name,
    setpointK: g.temperature,
    ...groupTemperature(atoms, g.indices, kineticEnergyOf),
  }));

/**
 * Temperature PROFILE along an axis: the present atoms binned by position
 * across their extent, each bin's kinetic temperature and count. This is how
 * a heat-conduction run is read — a hot band, a cold band, and the gradient
 * between them. Bins with no movable atom read 0 K.
 */
export const temperatureProfile = (
  atoms: readonly ThermostatAtomLike[],
  axis: 'x' | 'y' | 'z',
  bins: number,
  kineticEnergyOf: (a: ThermostatAtomLike) => number = kineticEnergyOfAtom,
): {
  axis: 'x' | 'y' | 'z';
  bins: { from: number; to: number; center: number; temperatureK: number; atomCount: number }[];
} => {
  const n = Math.max(2, Math.min(100, Math.round(bins)));
  let lo = Infinity;
  let hi = -Infinity;
  for (const a of atoms) {
    if (a.removed) continue;
    const v = a.position[axis];
    if (v < lo) lo = v;
    if (v > hi) hi = v;
  }
  if (!(hi > lo)) return { axis, bins: [] };
  const width = (hi - lo) / n;
  const ke = new Float64Array(n);
  const count = new Int32Array(n);
  for (const a of atoms) {
    if (a.removed || a.fixed) continue;
    let b = Math.floor((a.position[axis] - lo) / width);
    if (b >= n) b = n - 1;
    if (b < 0) b = 0;
    ke[b] += kineticEnergyOf(a);
    count[b]++;
  }
  const out = [];
  for (let b = 0; b < n; b++) {
    out.push({
      from: lo + b * width,
      to: lo + (b + 1) * width,
      center: lo + (b + 0.5) * width,
      temperatureK: count[b] > 0 ? (2 * ke[b] * UNIT_EV_OVER_KB) / (3 * count[b]) : 0,
      atomCount: count[b],
    });
  }
  return { axis, bins: out };
};

/**
 * Kinetic temperature of a set of atoms, K: T = 2·KE / (3·n·k_B) over the
 * movable members (3 degrees of freedom each; no center-of-mass or rigid-
 * constraint correction, which is what a partial set warrants). KE from
 * ½·m·v² in the engines' units via UNIT_EV_OVER_KB, as the engines' own
 * getCurrentTemperature does. Returns { temperatureK, atomCount } with 0 K
 * for an empty or resting set.
 */
export const groupTemperature = (
  atoms: readonly ThermostatAtomLike[],
  indices: readonly number[],
  kineticEnergyOf: (a: ThermostatAtomLike) => number = kineticEnergyOfAtom,
): { temperatureK: number; atomCount: number } => {
  let ke = 0;
  let n = 0;
  for (const i of indices) {
    const a = atoms[i];
    if (!a || a.removed || a.fixed) continue;
    ke += kineticEnergyOf(a);
    n++;
  }
  if (n === 0) return { temperatureK: 0, atomCount: 0 };
  return { temperatureK: (2 * ke * UNIT_EV_OVER_KB) / (3 * n), atomCount: n };
};
